import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { FormGroup, ReactiveFormsModule, FormControl, Validators } from '@angular/forms';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDividerModule } from '@angular/material/divider';
import { CommonModule } from '@angular/common';

import { Observable, combineLatest, map, Subscription, startWith, distinctUntilChanged, tap, finalize, of } from 'rxjs';
import { Assignee, Task, TaskComment, TaskHistory, TaskRequest, TaskStatus } from '@workspaces/models/task';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LoaderButtonComponent } from '../../loader-button/loader-button.component';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/state/app.state';
import { personaFeature, PersonaFetch } from '@workspaces/store/persona.feature';
import { ConfirmationModalComponent } from 'src/app/confirmation-modal/confirmation-modal.component';
import { workspacePersonaFeature, WorkspacePersonaFetch } from '@workspaces/store/workspace-persona.feature';
import { userManagementFeature, UserManagementFetch } from '@workspaces/store/user-management.feature';
import { PersonaWithMetadata, WorkspacePersona } from '@workspaces/models/personas';
import { Persona } from 'src/app/models/personas.interface';
import { KanbanIdPipe } from '@workspaces/utils/pipes/kanban-id.pipe';
import { MatTabsModule } from '@angular/material/tabs';
import { taskDetailsFeature, TaskDetailsFetch } from '@workspaces/store/task-details.feature';
import { WorkspaceUser } from '@workspaces/models/users';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { WorkspaceTaskService } from '@workspaces/services/task.service';
import { TaskHistoryComponent } from '../../task-history/task-history.component';
import { TaskCommentsComponent } from '../../task-comments/task-comments.component';
import { TagInputComponent } from '../../tag-input/tag-input.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ShortIdPipe } from '../../../utils/pipes/short-id.pipe';
import { Clipboard } from '@angular/cdk/clipboard';
import { WorkspaceAuthService } from '@workspaces/utils/workspace-auth.service';
import { MarkdownModule } from 'ngx-markdown';
import { WorkspaceFetch, workspaceListFeature } from '@workspaces/store/workspace-list.feature';

export interface TaskDialogData {
  taskId?: string;
  columnId?: string;
  workspaceId: string;
}

@Component({
  selector: 'app-task-details',
  templateUrl: './task-details.component.html',
  styleUrls: ['./task-details.component.scss', '../../../global-workspace-styles.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatDividerModule,
    MatTooltipModule,
    KanbanIdPipe,
    LoaderButtonComponent,
    MatTabsModule,
    MatProgressSpinner,
    TaskHistoryComponent,
    TaskCommentsComponent,
    TagInputComponent,
    MatIconModule,
    ShortIdPipe,
    MarkdownModule
  ]
})
export class TaskDetailsComponent implements OnInit, OnDestroy {
  private readonly sub = new Subscription();

  public readonly taskFormControls = {
    title: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.min(1)] }),
    description: new FormControl('', { nonNullable: true }),
    deadline: new FormControl<Date>(new Date(), { nonNullable: true, validators: [Validators.required] }),
    status: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.min(1)] }),
    responsibleId: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.min(1)] }),
    priority: new FormControl(3, { nonNullable: true, validators: [Validators.required, Validators.min(1)] }),
    tags: new FormControl<string[]>([])
  };
  public readonly taskForm = new FormGroup(this.taskFormControls);
  public taskHistory: TaskHistory[] = [];
  public taskComments: TaskComment[] = [];
  public assignees$!: Observable<Assignee[]>;
  public reporter$!: Observable<Assignee>;

  public isReadonly = false;

  public readonly statusOptions: TaskStatus[] = ['todo', 'in_progress', 'done', 'rejected'];
  public isSaving = false;
  public dialogTitle = 'Create New Task';
  public loading$!: Observable<boolean>;

  public readonly commentControl = new FormControl('', [Validators.required, Validators.min(1)]);
  public isPostingComment = false;
  public copyText = '';
  public useMdDescription = true;
  public isDisabledEdit$!: Observable<boolean>;

  @Input({ required: true }) data!: TaskDialogData;
  @Output() closeEventEmitter = new EventEmitter();

  constructor(
    private readonly store: Store<AppState>,
    private readonly workspaceTaskService: WorkspaceTaskService,
    private readonly workspaceAuthService: WorkspaceAuthService,
    private readonly snackbar: MatSnackBar,
    private readonly clipboard: Clipboard,
    private readonly dialog: MatDialog
  ) {}

  public get isCreate() {
    return !this.data.taskId;
  }

  public ngOnInit(): void {
    this.copyText = `${window.origin}/workspaces/${this.data.workspaceId}/task/${this.data.taskId}`;
    const user$ = this.workspaceAuthService.getWorkspaceStatusWithUser$();
    const taskData$ = this.data.taskId
      ? this.store.select(taskDetailsFeature.taskDetailsData(this.data.taskId))
      : of(null);

    this.sub.add(
      combineLatest([user$, taskData$]).subscribe(([user, task]) => {
        if (this.isCreate) {
          this.isReadonly = false;
        } else if (user && task) {
          const isAdmin = user.user?.role === 'admin';
          const isCreator = user.user?.id === task.created_by?.id || user.user?.cognito_id === task.created_by?.id;
          this.isReadonly = !(isAdmin || isCreator);
        } else {
          this.isReadonly = true;
        }
      })
    );
    this.fetchInitialRequiredData();
    const users$ = this.store.select(userManagementFeature.selectData);
    const persona$ = this.store.select(personaFeature.selectData);
    const workspacePersona$ = this.store.select(workspacePersonaFeature.workspacePersonaData(this.data.workspaceId));
    const personasWithMetadata$ = combineLatest([persona$, workspacePersona$]).pipe(
      map(([persona, workspacePersona]) => this.combinePersonas(workspacePersona, persona))
    );
    this.assignees$ = combineLatest([users$, personasWithMetadata$]).pipe(
      startWith([[], []]),
      distinctUntilChanged(),
      map(([users, personas]) => {
        return this.createUserPersonaDropdown(users, personas);
      })
    );
    if (this.data.taskId) {
      this.sub.add(
        this.store.select(taskDetailsFeature.taskDetailsData(this.data.taskId)).subscribe(taskDetails => {
          if (taskDetails) {
            this.setTaskForm(taskDetails);
          }
        })
      );
      this.reporter$ = combineLatest([
        this.assignees$,
        this.store.select(taskDetailsFeature.taskDetailsData(this.data.taskId))
      ]).pipe(map(([assignes, taskDetail]) => this.selectReporter(assignes, taskDetail)));
      this.store.dispatch(WorkspaceFetch({ workspaceId: this.data.workspaceId }));

      this.isDisabledEdit$ = this.store.select(workspaceListFeature.workspaceById(this.data.workspaceId)).pipe(
        map(workspace => !!workspace?.is_archived || !!workspace?.is_deleted),
        startWith(false),
        tap(isDisabledEdit => {
          if (isDisabledEdit) {
            this.taskForm.disable();
          } else {
            this.taskForm.enable();
          }
        })
      );
    }

    this.checkDataLoading();
  }

  public fetchInitialRequiredData() {
    if (this.data.taskId) {
      this.store.dispatch(TaskDetailsFetch({ workspaceId: this.data.workspaceId, taskId: this.data.taskId }));
    } else if (this.data.columnId) {
      this.taskFormControls.status.patchValue(this.data.columnId, { emitEvent: false });
    }
    this.store.dispatch(PersonaFetch({ includeHidden: true }));
    this.store.dispatch(WorkspacePersonaFetch({ workspaceId: this.data.workspaceId }));
    this.store.dispatch(UserManagementFetch({ workspaceId: this.data.workspaceId }));
  }

  public selectReporter(assignes: Assignee[], taskDetail: Task) {
    const defaultReporter: Assignee = {
      id: '',
      name: 'Unknown',
      type: 'user'
    };
    if (!taskDetail) {
      return defaultReporter;
    }
    return assignes.find(assigne => assigne.id === taskDetail.created_by.id) ?? defaultReporter;
  }

  public checkDataLoading() {
    const assignesLoading$ = combineLatest([
      this.store.select(userManagementFeature.userManagementIsLoading),
      this.store.select(personaFeature.personaIsLoading)
    ]);
    if (this.data.taskId) {
      this.loading$ = combineLatest([
        assignesLoading$,
        this.store.select(taskDetailsFeature.taskDetailsIsLoading(this.data.taskId))
      ]).pipe(
        map(([[userLoading, personaLoading], taskLoading]) => {
          return userLoading || personaLoading || taskLoading;
        })
      );
    } else {
      this.loading$ = assignesLoading$.pipe(map(([userLoading, personaLoading]) => userLoading || personaLoading));
    }
  }

  public createUserPersonaDropdown(users: WorkspaceUser[], personas: PersonaWithMetadata[]) {
    const userAssignes: Assignee[] = users.map(user => ({ id: user.id, name: user.email, type: 'user' }));
    const personaAssignes: Assignee[] = personas.map(persona => ({
      id: persona.id ?? '',
      name: persona.name ?? 'Unknown persona',
      type: 'persona'
    }));
    return [...userAssignes, ...personaAssignes];
  }

  public setTaskForm(taskDetails: Task) {
    const mappedDetails: (typeof this.taskForm)['value'] = {
      deadline: taskDetails.due_date ? new Date(taskDetails.due_date) : new Date(),
      description: taskDetails.description,
      responsibleId: taskDetails.assigned_to?.id ?? (taskDetails.assigned_to as unknown as string),
      status: taskDetails.status,
      title: taskDetails.name,
      priority: Number(taskDetails.priority),
      tags: taskDetails.tags
    };
    this.taskForm.patchValue(mappedDetails, { emitEvent: false });
    this.dialogTitle = `Edit Task: ${taskDetails.name}`;
  }

  public combinePersonas(workspacePersonas: WorkspacePersona[], personas: Persona[]): PersonaWithMetadata[] {
    if (!workspacePersonas || !personas) {
      return [];
    }

    return workspacePersonas.map(workspacePersona => {
      const personaWithMeta: PersonaWithMetadata = {
        ...workspacePersona,
        ...(personas.find(persona => persona.id === workspacePersona.persona_id) ?? {
          name: 'Unknown name',
          current_prompt: 'Unknown prompt',
          exampleDocuments: []
        })
      };
      return personaWithMeta;
    });
  }

  public onSave(): void {
    if (this.isSaving) {
      return;
    }
    if (this.taskForm.valid) {
      this.isSaving = true;
      const formValue = this.taskForm.getRawValue();
      const mappedForm: TaskRequest = {
        name: formValue.title ?? '',
        due_date: formValue.deadline.toISOString(),
        description: formValue.description,
        assigned_to_id: formValue.responsibleId,
        status: formValue.status as TaskStatus,
        priority: formValue.priority,
        tags: formValue.tags ?? []
      };

      if (this.data.taskId) {
        this.sub.add(
          this.workspaceTaskService
            .updateTask(this.data.workspaceId, this.data.taskId, mappedForm)
            .pipe(
              finalize(() => {
                this.isSaving = false;
              })
            )
            .subscribe(() => {
              this.snackbar.open('Task is updated!', 'Dismiss', {
                duration: 5000,
                panelClass: ['success-snackbar']
              });
              this.closeEventEmitter.emit();
            })
        );
      } else {
        this.sub.add(
          this.workspaceTaskService
            .createTask(this.data.workspaceId, mappedForm)
            .pipe(
              finalize(() => {
                this.isSaving = false;
              })
            )
            .subscribe(() => {
              this.snackbar.open('Task is created!', 'Dismiss', {
                duration: 5000,
                panelClass: ['success-snackbar']
              });
              this.closeEventEmitter.emit();
            })
        );
      }
    } else {
      this.taskForm.markAllAsTouched();
    }
  }

  public async copyLink() {
    const success = this.clipboard.copy(this.copyText);
    if (success) {
      this.snackbar.open('Link copied to clipboard', 'Dismiss', {
        duration: 3000,
        panelClass: ['success-snackbar']
      });
    } else {
      this.snackbar.open('Could not copy to clipboard', 'Dismiss', {
        duration: 3000,
        panelClass: ['error-snackbar']
      });
    }
  }

  public onDelete(): void {
    if (!this.data.taskId || this.isSaving) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      maxWidth: '400px',
      data: {
        message: 'Do you want to delete this task? This step is not reversible!'
      }
    });

    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (!result) {
        return;
      }
      this.isSaving = true;
      this.sub.add(
        this.workspaceTaskService
          .deleteTask(this.data.workspaceId, this.data.taskId!)
          .pipe(
            finalize(() => {
              this.isSaving = false;
            })
          )
          .subscribe(() => this.closeEventEmitter.emit())
      );
    });
  }

  public onCancel(): void {
    this.closeEventEmitter.emit();
  }

  public ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}
